import { useState, useCallback, useRef, useEffect, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDemoState } from "@/data/demoState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Play, Pause, X, ChevronRight, Zap,
  ShieldCheck, Truck, Smartphone, Send, Users, HardHat,
  AlertTriangle, CheckCircle2, Camera, ClipboardCheck, FileText, Eye
} from "lucide-react";

const heroSteps = [
  { id: 0, path: '/builder/stage-readiness', icon: ShieldCheck, tag: 'Readiness', title: 'Stage Readiness — Pre-Slab Gate', desc: 'Lot 42 Mambourin: 3 of 4 Pre-Slab conditions cleared. Waterproofing spec still under review. Trade readiness checks visible.', mutation: 'setProject' },
  { id: 1, path: '/builder/trade-release', icon: Truck, tag: 'Trade Release', title: 'Waterproofer is Ready to Release', desc: 'AquaSeal Waterproofing (Marco Vella) is ready. All other checks passed. Generate the SiteReady Trade Pass.', mutation: null },
  { id: 2, path: '/builder/trade-release', icon: Smartphone, tag: 'Generate', title: 'SiteReady Trade Pass Generated', desc: 'Trade Pass created for Marco Vella. Contains site notes, drawings, conditions, safety brief, and check-in capability.', mutation: 'generatePass' },
  { id: 3, path: '/builder/trade-release', icon: Send, tag: 'Send', title: 'Link Sent to Marco via WhatsApp', desc: 'SiteReady link dispatched to Marco Vella. He can now view the Trade Pack and check in when arriving on site.', mutation: 'sendLink' },
  { id: 4, path: '/builder/trade-pass/trade-042-wp', icon: Eye, tag: 'Tradie View', title: 'Marco Opens the SiteReady Link', desc: 'The tradie sees the mobile Trade Pack: site notes, relevant conditions, safety reminder, and the check-in button.', mutation: null },
  { id: 5, path: '/builder/trade-pass/trade-042-wp', icon: Users, tag: 'Check In', title: 'Waterproofer Checks In — 3 Crew', desc: 'Marco checks in with 3 crew members. On-Site Now board and Client Portal update instantly.', mutation: 'checkIn' },
  { id: 6, path: '/builder', icon: HardHat, tag: 'On-Site Now', title: 'Command Centre Updates', desc: 'On-Site Now board shows 2 trades on site: Concreter (5 crew) + Waterproofer (3 crew). 8 total crew.', mutation: null },
  { id: 7, path: '/shared/client-portal', icon: Eye, tag: 'Client', title: 'Client Portal — Trades Working Today', desc: 'The Henderson Family sees a clean, client-safe view: Concreter working, Waterproofer working. No internal details.', mutation: null },
  { id: 8, path: '/builder/trade-release', icon: AlertTriangle, tag: 'Cannot Start', title: 'Cannot Start — Proof Missing', desc: 'Marco reports: "Waterproofing membrane spec not approved — cannot commence wet area work." Blocker created.', mutation: 'cannotStart' },
  { id: 9, path: '/builder/job-blocker', icon: AlertTriangle, tag: 'Blocker', title: 'Job Blocker Board Updates', desc: 'New Cannot Start blocker appears. Supervisor notified. Trade is on-site but cannot commence until resolved.', mutation: null },
  { id: 10, path: '/builder/trade-release', icon: CheckCircle2, tag: 'Resolve', title: 'Supervisor Resolves the Blocker', desc: 'James Wheeler reviews the waterproofing spec and approves it. The Cannot Start is resolved. Trade can proceed.', mutation: 'resolve' },
  { id: 11, path: '/builder/trade-release', icon: Camera, tag: 'Work Proof', title: 'Work Proof Uploaded', desc: 'Marco uploads a photo: "Waterproofing_Complete.jpg" — wet area membrane fully applied per AquaSeal spec.', mutation: 'workProof' },
  { id: 12, path: '/builder/proof-review', icon: FileText, tag: 'Review', title: 'Proof Review Receives the Upload', desc: 'The work proof appears in the review queue. Supervisor can approve, flag, or request re-upload.', mutation: null },
  { id: 13, path: '/builder/trade-release', icon: ClipboardCheck, tag: 'Sign Off', title: 'Trade Signed Off by Supervisor', desc: 'James Wheeler signs off the Waterproofer. Work complete. Evidence recorded. Trade moves to Signed Off.', mutation: 'signOff' },
  { id: 14, path: '/builder/readiness-dossier', icon: FileText, tag: 'Dossier', title: 'Readiness Dossier Updated', desc: 'Signed-off work proof feeds into the Readiness Dossier. Full audit trail from release to completion.', mutation: null },
];

// Context so the trigger button can live anywhere
const HeroFlowContext = createContext({ startHeroFlow: () => {}, isHeroActive: false });
export function useHeroFlow() { return useContext(HeroFlowContext); }

export function HeroFlowProvider({ children }) {
  const navigate = useNavigate();
  const {
    setActiveProject, generateTradePass, sendTradePassLink,
    checkInTrade, markCannotStart, resolveCannotStart,
    submitWorkProof, signOffTrade
  } = useDemoState();

  const [step, setStep] = useState(-1);
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const isActive = step >= 0;
  const current = heroSteps[step] || null;

  const lastMutationStep = useRef(-1);

  const executeMutation = useCallback((mutationId, stepIdx) => {
    // Guard against double-fire from StrictMode
    if (lastMutationStep.current === stepIdx) return;
    lastMutationStep.current = stepIdx;
    switch (mutationId) {
      case 'setProject': setActiveProject('proj-042'); break;
      case 'generatePass': generateTradePass('trade-042-wp'); toast.success('SiteReady Trade Pass generated'); break;
      case 'sendLink': sendTradePassLink('trade-042-wp'); toast.success('Link sent to Marco Vella via WhatsApp'); break;
      case 'checkIn': checkInTrade('trade-042-wp', 3); toast.success('Waterproofer checked in — 3 crew on site'); break;
      case 'cannotStart': markCannotStart('trade-042-wp', 'Waterproofing membrane spec not approved — cannot commence wet area work'); toast.error('Cannot Start reported by Marco Vella'); break;
      case 'resolve': resolveCannotStart('trade-042-wp'); toast.success('Cannot Start resolved by supervisor'); break;
      case 'workProof': submitWorkProof('trade-042-wp', 'Waterproofing_Complete.jpg', 'Wet area membrane fully applied per AquaSeal spec'); toast.success('Work proof uploaded by Marco Vella'); break;
      case 'signOff': signOffTrade('trade-042-wp'); toast.success('Trade signed off by James Wheeler'); break;
      default: break;
    }
  }, [setActiveProject, generateTradePass, sendTradePassLink, checkInTrade, markCannotStart, resolveCannotStart, submitWorkProof, signOffTrade]);

  const advanceTo = useCallback((idx) => {
    if (idx >= heroSteps.length) {
      setRunning(false);
      return;
    }
    const s = heroSteps[idx];
    setStep(idx);
    navigate(s.path);
    if (s.mutation) {
      setTimeout(() => executeMutation(s.mutation, idx), 400);
    }
    // Fire completion toast on last step
    if (idx === heroSteps.length - 1) {
      setTimeout(() => {
        setRunning(false);
        toast.success('Hero Demo Complete', { description: 'Full Lot 42 Mambourin flow demonstrated.' });
      }, 800);
    }
  }, [navigate, executeMutation]);

  const startHeroFlow = useCallback(() => {
    setStep(0);
    setRunning(true);
    setPaused(false);
    setActiveProject('proj-042');
    navigate(heroSteps[0].path);
  }, [setActiveProject, navigate]);

  const goNext = useCallback(() => { advanceTo(step + 1); }, [step, advanceTo]);
  const goPrev = useCallback(() => {
    if (step > 0) { setStep(step - 1); navigate(heroSteps[step - 1].path); }
  }, [step, navigate]);

  const stop = useCallback(() => {
    setRunning(false); setStep(-1); setPaused(false);
    if (timer.current) clearTimeout(timer.current);
  }, []);

  // Auto-advance
  useEffect(() => {
    if (running && !paused && step >= 0 && step < heroSteps.length - 1) {
      timer.current = setTimeout(() => advanceTo(step + 1), 3500);
      return () => clearTimeout(timer.current);
    }
  }, [running, paused, step, advanceTo]);

  // Cleanup on unmount
  useEffect(() => { return () => { if (timer.current) clearTimeout(timer.current); }; }, []);

  return (
    <HeroFlowContext.Provider value={{ startHeroFlow, isHeroActive: isActive }}>
      {children}

      {/* Global hero flow bar — persists across all routes */}
      {isActive && current && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] flex justify-center px-4 pb-5 pointer-events-none" data-testid="hero-flow-bar">
          <div className="pointer-events-auto bg-gradient-to-r from-indigo-950 to-violet-950 text-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-indigo-800/50">
            <div className="flex gap-0.5 px-5 pt-3">
              {heroSteps.map((_, i) => (
                <div key={i} className={`h-1 rounded-full flex-1 transition-all duration-500 ${i <= step ? 'bg-indigo-400' : 'bg-indigo-900'}`} />
              ))}
            </div>
            <div className="px-5 pt-2.5 pb-3.5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Lot 42 Mambourin Hero Flow</span>
                    <span className="text-[10px] text-indigo-300">Step {step + 1}/{heroSteps.length}</span>
                    {current.tag && <Badge className="text-[8px] bg-indigo-800 text-indigo-300 border-indigo-700">{current.tag}</Badge>}
                    {running && !paused && <span className="text-[9px] text-violet-400 animate-pulse flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> Auto-advancing</span>}
                  </div>
                  <div className="flex items-center gap-2 mb-0.5">
                    {current && <current.icon className="w-4 h-4 text-indigo-300 shrink-0" />}
                    <h3 className="text-sm font-bold text-white">{current.title}</h3>
                  </div>
                  <p className="text-xs text-indigo-200/80 leading-relaxed">{current.desc}</p>
                </div>
                <button onClick={stop} className="text-indigo-500 hover:text-indigo-300 transition-colors p-1 shrink-0" data-testid="hero-flow-stop"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-indigo-800/50">
                <button onClick={goPrev} disabled={step === 0} className="text-xs text-indigo-400 hover:text-white disabled:opacity-30 transition-colors" data-testid="hero-prev">Back</button>
                <div className="flex items-center gap-2">
                  <button onClick={() => { if (running && !paused) { setPaused(true); } else { setRunning(true); setPaused(false); } }}
                    className="text-xs text-indigo-400 hover:text-white flex items-center gap-1" data-testid="hero-play-pause">
                    {running && !paused ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    {running && !paused ? 'Pause' : 'Auto-play'}
                  </button>
                  <button onClick={stop} className="text-[10px] text-indigo-600 hover:text-indigo-300 px-2">End</button>
                  <Button size="sm" onClick={goNext} disabled={step >= heroSteps.length - 1}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white gap-1 rounded-lg text-xs" data-testid="hero-next">
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </HeroFlowContext.Provider>
  );
}

// Trigger button — place this anywhere
export function HeroFlowTrigger() {
  const { startHeroFlow, isHeroActive } = useHeroFlow();
  if (isHeroActive) return null;
  return (
    <Button onClick={startHeroFlow} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white gap-2 shadow-lg shadow-indigo-200" data-testid="hero-flow-start-btn">
      <Play className="w-4 h-4" /> Run Hero Demo
    </Button>
  );
}
